import { useRef, useCallback } from "react";
import { computePosition, autoUpdate, flip, shift, offset } from "@floating-ui/dom";

/**
 * Anchors a portal dropdown to a reference button using floating-ui.
 *
 * Returns a ref for the anchor button and a callback ref for the floating
 * element. When the floating element mounts, positioning is applied directly
 * to its DOM style (no React re-renders). autoUpdate keeps it in sync on
 * scroll and resize. Cleanup runs automatically on unmount.
 *
 * @param {boolean} portal - When false the refs are inert (non-portal mode).
 * @param {object}  options
 * @param {string}  options.placement  - floating-ui placement (default: "bottom-start")
 * @param {number}  options.offsetPx   - gap between anchor and floating (default: 4)
 * @param {number}  options.shiftPadding - viewport edge padding for shift middleware (default: 8)
 */
export function useFloatingPortal(
  portal,
  { placement = "bottom-start", offsetPx = 4, shiftPadding = 8 } = {}
) {
  const buttonRef = useRef(null);
  const cleanupRef = useRef(null);

  const setFloatingRef = useCallback(
    (el) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      if (!el || !buttonRef.current || !portal) return;

      el.style.position = "fixed";
      el.style.margin = "0";
      el.style.zIndex = "9999";

      const update = () => {
        if (!buttonRef.current) return;
        computePosition(buttonRef.current, el, {
          placement,
          middleware: [offset(offsetPx), flip(), shift({ padding: shiftPadding })],
        }).then(({ x, y }) => {
          el.style.left = `${x}px`;
          el.style.top = `${y}px`;
        });
      };

      update();
      cleanupRef.current = autoUpdate(buttonRef.current, el, update);
    },
    [portal, placement, offsetPx, shiftPadding]
  );

  return { buttonRef, setFloatingRef };
}
